'use client'

import clsx from 'clsx'
import { FC, useState } from 'react'
import { createPortal } from 'react-dom'

import { Checkbox } from '@/shared/ui/checkbox'
import { Icon } from '@/shared/ui/icon'
import { Popover } from '@/shared/ui/Popover'

import { AppliedFilterBlock } from '../../types/types'

import cls from './applied-filter-chip.module.scss'

type AppliedFilterChipProps = {
	block: AppliedFilterBlock
	isDisabled?: boolean
	onToggleValue: (code: string, value: string | number) => void
	onDelete: (code: string) => void
}

// Applied-filter chip: shows the block title. List blocks open a dropdown (duplicating the sidebar
// values) so a value can be toggled inline; the × removes the whole block.
export const AppliedFilterChip: FC<AppliedFilterChipProps> = ({ block, isDisabled, onToggleValue, onDelete }) => {
	const [open, setOpen] = useState(false)

	const handleDelete = (e: React.MouseEvent) => {
		// Stop the click from bubbling to the chip body (which toggles the dropdown popover).
		e.stopPropagation()
		onDelete(block.code)
	}

	const chip = (
		<div
			className={clsx(cls.chip, {
				[cls.chip_open]: open && block.hasDropdown,
				[cls.chip_disabled]: isDisabled
			})}>
			{block.hasDropdown ? (
				<button type="button" className={cls.body} disabled={isDisabled}>
					<span className={cls.name}>{block.name}</span>
					<Icon className={cls.caret} name={open ? 'ArrowUp' : 'ArrowDown'} size={16} />
				</button>
			) : (
				<span className={cls.body}>
					<span className={cls.name}>{block.name}</span>
				</span>
			)}

			<button
				type="button"
				className={cls.delete}
				onClick={handleDelete}
				disabled={isDisabled}
				aria-label={`Удалить фильтр «${block.name}»`}>
				<Icon name="Close" size={12} color="#292424" />
			</button>
		</div>
	)

	if (!block.hasDropdown) return chip

	return (
		<>
			{/* Mobile-only presentational scrim behind the bottom-sheet dropdown (hidden on desktop via CSS).
			    Dismissal is owned by the Popover's click-outside; the scrim carries no handler. */}
			{open && typeof document !== 'undefined' && createPortal(<div className={cls.scrim} />, document.body)}

			<Popover
				trigger="click"
				placement="bottomLeft"
				arrow={false}
				open={open}
				onOpenChange={next => !isDisabled && setOpen(next)}
				overlayClassName={cls.overlay}
				content={
					<div className={cls.dropdown}>
						{block.values.map(({ ID, NAME, VALUE, FILTERED_VALUE, CHECKED, ELEMENT_COUNT }) => (
							<Checkbox
								key={ID}
								label={NAME ?? String(VALUE)}
								checked={!!CHECKED}
								disabled={!ELEMENT_COUNT || isDisabled}
								onChange={() => onToggleValue(block.code, FILTERED_VALUE ?? VALUE)}
							/>
						))}
					</div>
				}>
				{chip}
			</Popover>
		</>
	)
}
